import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import SessionNotes from '../components/SessionNotes';
import LifeObjectives from '../components/LifeObjectives';
import RecommendedReadings from '../components/RecommendedReadings';
import Trending from '../components/Trending';
import CoachingInsights from '../components/CoachingInsights';
import ProfileSettings from '../components/ProfileSettings';
import WheelOfLife from '../components/WheelOfLife';
import WeeklyReflectionManager from '../components/WeeklyReflectionManager';
import ClientOverview from '../components/ClientOverview';
import NextSessionPlan from '../components/NextSessionPlan';
import PrivateNotes from '../components/PrivateNotes';
import UpcomingSessions from '../components/UpcomingSessions';
import {
  Lightbulb, Maximize2, Minimize2, FileText, Target, BookOpen,
  TrendingUp, ChevronRight, Calendar, CalendarDays, User, Settings, LogOut, ArrowLeft, Sun, Moon, Compass, MessageSquare, Eye, Lock
} from 'lucide-react';
import rmmbrLogo from '../assets/rmmbr.png';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function CoachDashboard() {
  const { user, logout, token, loading: authLoading } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { clientId } = useParams();
  
  // Get current CSS variable values to force React to re-render when they change
  const [cssVars, setCssVars] = useState(() => {
    if (typeof window !== 'undefined') {
      const root = getComputedStyle(document.documentElement);
      return {
        bgPrimary: root.getPropertyValue('--bg-primary').trim(),
        bgSecondary: root.getPropertyValue('--bg-secondary').trim(),
        bgTertiary: root.getPropertyValue('--bg-tertiary').trim(),
        textPrimary: root.getPropertyValue('--text-primary').trim()
      };
    }
    return { bgPrimary: '#1a1816', bgSecondary: '#2a2520', bgTertiary: '#1f1d1b', textPrimary: '#ffffff' };
  });
  
  useEffect(() => {
    // Update CSS vars state when theme changes
    if (typeof window !== 'undefined') {
      const root = getComputedStyle(document.documentElement);
      setCssVars({
        bgPrimary: root.getPropertyValue('--bg-primary').trim(),
        bgSecondary: root.getPropertyValue('--bg-secondary').trim(),
        bgTertiary: root.getPropertyValue('--bg-tertiary').trim(),
        textPrimary: root.getPropertyValue('--text-primary').trim()
      });
    }
    // Only update when theme changes, no polling needed
  }, [theme]);
  
  const navigate = useNavigate();
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedView, setSelectedView] = useState('overview'); // 'overview', 'notes', 'breakthroughs', 'trends', 'sessionPlan', etc.
  const [selectedItem, setSelectedItem] = useState(null);
  const [notesExpanded, setNotesExpanded] = useState(true);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatExpanded, setChatExpanded] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [viewMode, setViewMode] = useState('coach'); // 'coach' or 'client'
  const [includePrivateNotes, setIncludePrivateNotes] = useState(false);
  const chatEndRef = useRef(null);
  const profileMenuRef = useRef(null);

  // Set up axios interceptor to add token to all requests
  useEffect(() => {
    const interceptor = axios.interceptors.request.use(
      (config) => {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.request.eject(interceptor);
    };
  }, [token]);

  useEffect(() => {
    // Wait for auth to finish loading before fetching client
    if (!authLoading && user && token && clientId) {
      fetchClient(clientId);
    }
  }, [authLoading, user, token, clientId]);

  useEffect(() => {
    if (selectedClient) {
      fetchClientData(selectedClient.id);
      // Clear chat when switching clients
      setChatMessages([]);
    }
  }, [selectedClient]);

  // Auto-scroll chat to bottom when new messages arrive
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, chatLoading]);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchClient = async (id) => {
    try {
      // Get basic client info from the list endpoint
      const clientsResponse = await axios.get(`${API_URL}/clients`);
      const client = clientsResponse.data.find(c => c.id === parseInt(id));
      if (client) {
        setSelectedClient(client);
      } else {
        // Client not found, redirect to CRM
        navigate('/coach');
      }
    } catch (error) {
      console.error('Error fetching client:', error);
      navigate('/coach');
    }
  };

  const fetchClientData = async (clientId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/clients/${clientId}`);
      setClientData(response.data);
      return response.data; // Return the data so callbacks can use it
    } catch (error) {
      console.error('Error fetching client data:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };


  const handleSessionNotesUpload = async (content) => {
    try {
      const response = await axios.post(
        `${API_URL}/clients/${selectedClient.id}/session-notes`,
        { content }
      );
      await fetchClientData(selectedClient.id);
      return response.data;
    } catch (error) {
      console.error('Error uploading session recap:', error);
      throw error;
    }
  };

  const handleAddReading = async (title, author, notes) => {
    try {
      await axios.post(`${API_URL}/clients/${selectedClient.id}/readings`, {
        title,
        author,
        notes
      });
      await fetchClientData(selectedClient.id);
    } catch (error) {
      console.error('Error adding reading:', error);
    }
  };

  const handleUpdateReading = async (id, title, author, notes) => {
    try {
      await axios.put(`${API_URL}/readings/${id}`, { title, author, notes });
      await fetchClientData(selectedClient.id);
    } catch (error) {
      console.error('Error updating reading:', error);
    }
  };

  const handleDeleteReading = async (id) => {
    try {
      await axios.delete(`${API_URL}/readings/${id}`);
      await fetchClientData(selectedClient.id);
    } catch (error) {
      console.error('Error deleting reading:', error);
    }
  };

  const handleUpdateObjective = async (id, objective, status, category, timeline) => {
    try {
      await axios.put(`${API_URL}/objectives/${id}`, { objective, status, category, timeline });
      await fetchClientData(selectedClient.id);
    } catch (error) {
      console.error('Error updating objective:', error);
    }
  };

  const handleAddObjective = async (objectiveData) => {
    try {
      await axios.post(`${API_URL}/objectives`, {
        client_id: selectedClient.id,
        objective: objectiveData.objective,
        category: objectiveData.category,
        timeline: objectiveData.timeline
      });
      await fetchClientData(selectedClient.id);
    } catch (error) {
      console.error('Error adding objective:', error);
      toast.error('Failed to add objective');
    }
  };

  const handleDeleteObjective = async (id) => {
    try {
      await axios.delete(`${API_URL}/objectives/${id}`);
      await fetchClientData(selectedClient.id);
    } catch (error) {
      console.error('Error deleting objective:', error);
    }
  };

  const handleRecategorizeObjectives = async () => {
    try {
      const response = await axios.post(`${API_URL}/clients/${selectedClient.id}/recategorize-objectives`);
      toast(`Recategorized ${response.data.updated} objectives!`);
      await fetchClientData(selectedClient.id);
    } catch (error) {
      console.error('Error recategorizing objectives:', error);
      toast.error('Failed to recategorize objectives');
    }
  };

  const handleSendChatMessage = async (e) => {
    e?.preventDefault();
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput('');

    // Add user message to chat
    const newUserMessage = { role: 'user', content: userMessage, timestamp: new Date() };
    setChatMessages(prev => [...prev, newUserMessage]);
    setChatLoading(true);

    try {
      // Build conversation history for context
      const conversationHistory = chatMessages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const response = await axios.post(`${API_URL}/chat`, {
        message: userMessage,
        clientId: selectedClient?.id,
        conversationHistory: conversationHistory,
        includePrivateNotes: includePrivateNotes
      });

      // Add AI response to chat
      const aiMessage = { 
        role: 'assistant', 
        content: response.data.response, 
        timestamp: new Date() 
      };
      setChatMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending chat message:', error);
      const errorMessage = { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.', 
        timestamp: new Date(),
        error: true
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setChatLoading(false);
    }
  };

  return (
    <div 
      key={`dashboard-${theme}-${cssVars.bgPrimary}`}
      className="h-screen flex flex-col" 
      style={{ 
        backgroundColor: cssVars.bgPrimary || 'var(--bg-primary)'
      }}
    >
      {/* Top Navigation Bar */}
      <nav 
        className="border-b flex-shrink-0"
        style={{ 
          backgroundColor: cssVars.bgPrimary || 'var(--bg-primary)',
          borderColor: 'var(--border-color)',
          position: 'relative',
          zIndex: 100
        }}
      >
        <div className="px-6 py-3">
          <div className="flex justify-between items-center" style={{ position: 'relative', zIndex: 101 }}>
            {/* Logo and Back Button */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/coach')}
                style={{ color: 'var(--text-secondary)' }}
                className="flex items-center gap-2 transition-colors"
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text-primary)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="font-medium">Back to CRM</span>
              </button>
              <div className="h-6 w-px" style={{ backgroundColor: 'var(--border-color)' }}></div>
              <img src={rmmbrLogo} alt="rmmbr" className="h-5 w-auto" />
            </div>

            {/* Client Name */}
            {selectedClient && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium" style={{ color: 'var(--text-tertiary)' }}>Client:</span>
                <span className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>{selectedClient.name}</span>
              </div>
            )}

            {/* View Mode Toggle, Theme Toggle and Profile Menu */}
            <div className="flex items-center gap-3" style={{ position: 'relative', zIndex: 1000 }}>
              {/* View Mode Toggle */}
              {selectedClient && (
                <div className="flex items-center gap-1 bg-[var(--bg-tertiary)] rounded-lg p-1">
                  <button
                    type="button"
                    onClick={() => setViewMode('coach')}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-all duration-200 ${
                      viewMode === 'coach'
                        ? 'bg-[var(--accent)] text-white'
                        : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                    }`}
                    title="Coach View - See all coaching features"
                  >
                    Coach
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('client')}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-all duration-200 flex items-center gap-1 ${
                      viewMode === 'client'
                        ? 'bg-[var(--accent)] text-white'
                        : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                    }`}
                    title="Client View - Preview what clients see"
                  >
                    <Eye className="w-4 h-4" />
                    <span>Client</span>
                  </button>
                </div>
              )}

              <button
                type="button"
                onClick={() => toggleTheme()}
                className="p-2 rounded-lg transition-all duration-200 cursor-pointer"
                style={{
                  color: 'var(--text-tertiary)',
                  position: 'relative',
                  zIndex: 1001,
                  pointerEvents: 'auto',
                  backgroundColor: 'transparent',
                  border: 'none',
                  outline: 'none',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                  e.currentTarget.style.color = 'var(--accent)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--text-tertiary)';
                }}
                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>
              <div className="relative" ref={profileMenuRef}>
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center gap-2 p-1 rounded-full hover:bg-[var(--bg-secondary)] transition-all duration-200 border-2 border-[var(--accent)] focus:outline-none"
                >
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--accent-20)' }}>
                    <User className="w-5 h-5" style={{ color: 'var(--accent)' }} />
                  </div>
                </button>

                {/* Profile Dropdown Menu */}
                {showProfileMenu && (
                  <div
                    className="absolute right-0 mt-2 w-56 rounded-lg py-2 z-50 animate-in fade-in duration-200"
                    style={{
                      backgroundColor: 'var(--bg-secondary)',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      borderColor: 'var(--border-color)',
                      boxShadow: 'var(--shadow-lg)'
                    }}
                  >
                    <div className="px-4 py-3 border-b border-[var(--border-color)]">
                      <p className="text-sm font-semibold text-[var(--text-primary)]">{user?.name}</p>
                      <p className="text-xs text-[var(--text-secondary)] mt-0.5">{user?.email}</p>
                    </div>
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        setShowProfileSettings(true);
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)] transition-colors flex items-center gap-3"
                    >
                      <Settings className="w-4 h-4" />
                      <span>Profile Settings</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        logout();
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm text-red-400 hover:bg-[var(--bg-tertiary)] hover:text-red-300 transition-colors flex items-center gap-3"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {!clientId ? (
        <div className="flex-1 flex justify-center items-center">
          <div className="text-center">
            <p className="text-[var(--text-secondary)] mb-4">No client selected</p>
            <button
              onClick={() => navigate('/coach')}
              className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white px-6 py-3 rounded-lg font-semibold transition-all"
            >
              Go to CRM
            </button>
          </div>
        </div>
      ) : selectedClient && !loading && clientData ? (
        <div className="flex-1 flex overflow-hidden min-h-0 min-w-0">
          {/* Left Sidebar - Navigation */}
          <div 
            className="w-80 border-r flex flex-col flex-shrink-0"
            style={{
              borderColor: 'var(--border-color)',
              backgroundColor: 'var(--bg-tertiary)'
            }}
          >
            {/* Header */}
            <div 
              className="px-6 py-4 border-b flex-shrink-0"
              style={{ borderColor: 'var(--border-color)' }}
            >
              <h2 
                className="text-xs font-semibold uppercase tracking-wider"
                style={{ color: 'var(--text-secondary)' }}
              >
                Navigation
              </h2>
            </div>

            {/* Navigation Items */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {/* Main Navigation - Top Level Flow */}
              <div className="px-4 py-3 space-y-1">
                {/* 1. Client Overview - NEW HOME TAB */}
                <button
                  onClick={() => {
                    setSelectedView('overview');
                    setSelectedItem(null);
                  }}
                  className={`w-full text-left px-4 py-2.5 rounded-lg transition-all duration-200 font-medium flex items-center gap-3 ${
                    selectedView === 'overview'
                      ? 'bg-[var(--accent)]/15 text-[var(--accent)] shadow-sm'
                      : 'text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]'
                  }`}
                >
                  <User className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1">Client Overview</span>
                </button>

                {/* 1.5 Upcoming Sessions */}
                <button
                  onClick={() => {
                    setSelectedView('upcoming-sessions');
                    setSelectedItem(null);
                  }}
                  className={`w-full text-left px-4 py-2.5 rounded-lg transition-all duration-200 font-medium flex items-center gap-3 ${
                    selectedView === 'upcoming-sessions'
                      ? 'bg-[var(--accent)]/15 text-[var(--accent)] shadow-sm'
                      : 'text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]'
                  }`}
                >
                  <CalendarDays className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1">Upcoming Sessions</span>
                </button>

                {/* 2. Session Notes */}
                <button
                  onClick={() => {
                    setNotesExpanded(!notesExpanded);
                    setSelectedView('notes');
                    setSelectedItem(null);
                  }}
                  className={`w-full text-left px-4 py-2.5 rounded-lg transition-all duration-200 font-medium flex items-center gap-3 group ${
                    selectedView === 'notes' && !selectedItem ? 'shadow-sm' : ''
                  }`}
                  style={{
                    backgroundColor: selectedView === 'notes' && !selectedItem
                      ? 'var(--accent-15)'
                      : 'transparent',
                    color: selectedView === 'notes' && !selectedItem
                      ? 'var(--accent)'
                      : 'var(--text-secondary)'
                  }}
                  onMouseEnter={(e) => {
                    if (!(selectedView === 'notes' && !selectedItem)) {
                      e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                      e.currentTarget.style.color = 'var(--text-primary)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!(selectedView === 'notes' && !selectedItem)) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = 'var(--text-secondary)';
                    }
                  }}
                >
                  <ChevronRight
                    className={`w-4 h-4 transition-transform duration-200 flex-shrink-0 ${
                      notesExpanded ? 'rotate-90' : ''
                    }`}
                  />
                  <FileText className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1">Session Notes</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    selectedView === 'notes' && !selectedItem
                      ? 'bg-[var(--accent)]/20 text-[var(--accent)]'
                      : 'bg-[var(--bg-secondary)] text-[var(--text-tertiary)]'
                  }`}>
                    {clientData.sessionNotes.length}
                  </span>
                </button>

                {notesExpanded && (
                  <div className="ml-8 mt-1 space-y-0.5 border-l border-[var(--border-color)] pl-3">
                    {clientData.sessionNotes.map((note) => (
                      <button
                        key={note.id}
                        onClick={() => {
                          setSelectedView('notes');
                          setSelectedItem(note);
                        }}
                        className={`w-full text-left px-3 py-1.5 rounded-md transition-all duration-150 text-sm flex items-center gap-2 ${
                          selectedItem?.id === note.id && selectedView === 'notes'
                            ? 'bg-[var(--accent)]/15 text-[var(--accent)]'
                            : 'text-[var(--text-tertiary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-secondary)]'
                        }`}
                      >
                        <Calendar className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">
                        {note.session_date || new Date(note.created_at).toLocaleDateString()}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {/* 3. Session Insights */}
                {viewMode === 'coach' && (
                  <button
                    onClick={() => {
                      setSelectedView('breakthroughs');
                      setSelectedItem(null);
                    }}
                    className={`w-full text-left px-4 py-2.5 rounded-lg transition-all duration-200 font-medium flex items-center gap-3 ${
                      selectedView === 'breakthroughs' ? 'shadow-sm' : ''
                    }`}
                    style={{
                      backgroundColor: selectedView === 'breakthroughs'
                        ? 'var(--accent-15)'
                        : 'transparent',
                      color: selectedView === 'breakthroughs'
                        ? 'var(--accent)'
                        : 'var(--text-secondary)'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedView !== 'breakthroughs') {
                        e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                        e.currentTarget.style.color = 'var(--text-primary)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedView !== 'breakthroughs') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = 'var(--text-secondary)';
                      }
                    }}
                  >
                    <Lightbulb className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1">Session Insights</span>
                  </button>
                )}

                {/* 4. Longterm Trends */}
                {viewMode === 'coach' && (
                  <button
                    onClick={() => {
                      setSelectedView('trends');
                      setSelectedItem(null);
                    }}
                    className={`w-full text-left px-4 py-2.5 rounded-lg transition-all duration-200 font-medium flex items-center gap-3 ${
                      selectedView === 'trends' ? 'shadow-sm' : ''
                    }`}
                    style={{
                      backgroundColor: selectedView === 'trends'
                        ? 'var(--accent-15)'
                        : 'transparent',
                      color: selectedView === 'trends'
                        ? 'var(--accent)'
                        : 'var(--text-secondary)'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedView !== 'trends') {
                        e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                        e.currentTarget.style.color = 'var(--text-primary)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedView !== 'trends') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = 'var(--text-secondary)';
                      }
                    }}
                  >
                    <TrendingUp className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1">Longterm Trends</span>
                  </button>
                )}

                {/* 5. Next Session Plan */}
                {viewMode === 'coach' && (
                  <button
                    onClick={() => {
                      setSelectedView('sessionPlan');
                      setSelectedItem(null);
                    }}
                    className={`w-full text-left px-4 py-2.5 rounded-lg transition-all duration-200 font-medium flex items-center gap-3 ${
                      selectedView === 'sessionPlan'
                        ? 'bg-[var(--accent)]/15 text-[var(--accent)] shadow-sm'
                        : 'text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]'
                    }`}
                  >
                    <Calendar className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1">Next Session Plan</span>
                  </button>
                )}

                {/* 6. Private Coach Notes - NEW */}
                {viewMode === 'coach' && (
                  <button
                    onClick={() => {
                      setSelectedView('privateNotes');
                      setSelectedItem(null);
                    }}
                    className={`w-full text-left px-4 py-2.5 rounded-lg transition-all duration-200 font-medium flex items-center gap-3 ${
                      selectedView === 'privateNotes'
                        ? 'bg-[var(--accent)]/15 text-[var(--accent)] shadow-sm'
                        : 'text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]'
                    }`}
                  >
                    <Lock className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1">Private Coach Notes</span>
                  </button>
                )}
              </div>

              {/* Divider */}
              <div className="px-4 py-2">
                <div className="border-t" style={{ borderColor: 'var(--border-color)' }}></div>
              </div>

              {/* Bottom Section - Weekly Reflections & Tools */}
              <div className="px-4 py-3 space-y-1">
                <div className="px-4 py-1 mb-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                    Tools & Resources
                  </h3>
                </div>

                {/* 7. Weekly Reflections */}
                <button
                  onClick={() => {
                    setSelectedView('weeklyReflections');
                    setSelectedItem(null);
                  }}
                  className={`w-full text-left px-4 py-2.5 rounded-lg transition-all duration-200 font-medium flex items-center gap-3 ${
                    selectedView === 'weeklyReflections'
                      ? 'bg-[var(--accent)]/15 text-[var(--accent)] shadow-sm'
                      : 'text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]'
                  }`}
                >
                  <MessageSquare className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1">Weekly Reflections</span>
                </button>

                {/* 8. Life Board (Objectives) - HIDDEN FOR NOW */}
                {false && (
                  <button
                    onClick={() => {
                      setSelectedView('objectives');
                      setSelectedItem(null);
                    }}
                    className={`w-full text-left px-4 py-2.5 rounded-lg transition-all duration-200 font-medium flex items-center gap-3 ${
                      selectedView === 'objectives'
                        ? 'bg-[var(--accent)]/15 text-[var(--accent)] shadow-sm'
                        : 'text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]'
                    }`}
                  >
                    <Target className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1">Life Board</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      selectedView === 'objectives'
                        ? 'bg-[var(--accent)]/20 text-[var(--accent)]'
                        : 'bg-[var(--bg-secondary)] text-[var(--text-tertiary)]'
                    }`}>
                      {clientData.objectives.length}
                    </span>
                  </button>
                )}

                {/* 9. RMMBR Circle */}
                <button
                  onClick={() => {
                    setSelectedView('wheel');
                    setSelectedItem(null);
                  }}
                  className={`w-full text-left px-4 py-2.5 rounded-lg transition-all duration-200 font-medium flex items-center gap-3 ${
                    selectedView === 'wheel'
                      ? 'bg-[var(--accent)]/15 text-[var(--accent)] shadow-sm'
                      : 'text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]'
                  }`}
                >
                  <Compass className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1">RMMBR Circle</span>
                </button>

                {/* Reading List */}
                <button
                  onClick={() => {
                    setSelectedView('readings');
                    setSelectedItem(null);
                  }}
                  className={`w-full text-left px-4 py-2.5 rounded-lg transition-all duration-200 font-medium flex items-center gap-3 ${
                    selectedView === 'readings'
                      ? 'bg-[var(--accent)]/15 text-[var(--accent)] shadow-sm'
                      : 'text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]'
                  }`}
                >
                  <BookOpen className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1">Reading List</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    selectedView === 'readings'
                      ? 'bg-[var(--accent)]/20 text-[var(--accent)]'
                      : 'bg-[var(--bg-secondary)] text-[var(--text-tertiary)]'
                  }`}>
                    {clientData.readings.length}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Middle Panel - Detail View */}
          <div 
            className="flex flex-col overflow-hidden min-h-0 min-w-0 flex-1 transition-all duration-300"
            style={{ backgroundColor: cssVars.bgPrimary || 'var(--bg-primary)' }}
          >
            <div className="flex-1 overflow-y-auto p-8 min-h-0">
              {selectedView === 'overview' && (
                <ClientOverview
                  clientId={selectedClient.id}
                  clientName={selectedClient.name}
                  clientData={clientData}
                  onNavigate={setSelectedView}
                />
              )}
              {selectedView === 'upcoming-sessions' && (
                <UpcomingSessions
                  onSelectClient={(client) => {
                    setSelectedClient(client);
                    setSelectedView('overview');
                    fetchClientData(client.id);
                  }}
                />
              )}
              {selectedView === 'notes' && (
                <SessionNotes
                  sessionNotes={clientData.sessionNotes}
                  onUpload={handleSessionNotesUpload}
                  selectedNote={selectedItem}
                  isClientView={viewMode === 'client'}
                  onPublishToggle={async () => {
                    const refreshedData = await fetchClientData(selectedClient.id);
                    // Update selectedItem to the refreshed note if it still exists
                    if (selectedItem && refreshedData) {
                      const updatedNote = refreshedData.sessionNotes.find(n => n.id === selectedItem.id);
                      if (updatedNote) {
                        setSelectedItem(updatedNote);
                      }
                    }
                  }}
                  onDelete={() => {
                    setSelectedItem(null);
                    fetchClientData(selectedClient.id);
                  }}
                  onUpdate={async () => {
                    const refreshedData = await fetchClientData(selectedClient.id);
                    // Update selectedItem to the refreshed note if it still exists
                    if (selectedItem && refreshedData) {
                      const updatedNote = refreshedData.sessionNotes.find(n => n.id === selectedItem.id);
                      if (updatedNote) {
                        setSelectedItem(updatedNote);
                      }
                    }
                  }}
                />
              )}
              {(selectedView === 'breakthroughs' || selectedView === 'insights') && (
                <CoachingInsights
                  clientId={selectedClient.id}
                  clientName={selectedClient.name}
                  onRefreshData={() => fetchClientData(selectedClient.id)}
                />
              )}
              {(selectedView === 'trends' || selectedView === 'trending') && (
                <Trending clientId={selectedClient.id} />
              )}
              {selectedView === 'sessionPlan' && (
                <NextSessionPlan
                  clientId={selectedClient.id}
                  clientName={selectedClient.name}
                />
              )}
              {selectedView === 'privateNotes' && (
                <PrivateNotes
                  clientId={selectedClient.id}
                  clientName={selectedClient.name}
                />
              )}
              {selectedView === 'weeklyReflections' && (
                <WeeklyReflectionManager
                  clientId={selectedClient.id}
                  clientName={selectedClient.name}
                />
              )}
              {selectedView === 'objectives' && (
                <LifeObjectives
                  objectives={clientData.objectives}
                  onUpdate={handleUpdateObjective}
                  onDelete={handleDeleteObjective}
                  selectedObjective={selectedItem}
                  onRecategorize={handleRecategorizeObjectives}
                  clientId={selectedClient.id}
                  onAdd={handleAddObjective}
                  isClientView={viewMode === 'client'}
                />
              )}
              {selectedView === 'wheel' && (
                <WheelOfLife clientId={selectedClient.id} isCoach={viewMode === 'coach'} />
              )}
              {selectedView === 'readings' && (
                <RecommendedReadings
                  readings={clientData.readings}
                  onAdd={handleAddReading}
                  onUpdate={handleUpdateReading}
                  onDelete={handleDeleteReading}
                  selectedReading={selectedItem}
                  isClientView={viewMode === 'client'}
                />
              )}
            </div>
          </div>

          {/* Right Sidebar - AI Chat */}
          <div
            className={`border-l flex flex-col flex-shrink-0 transition-all duration-300 ${
              chatExpanded ? 'w-[600px]' : 'w-96'
            }`}
            style={{
              borderColor: 'var(--border-color)',
              backgroundColor: cssVars.bgPrimary || 'var(--bg-primary)'
            }}
          >
            <div
              className="px-6 py-4 border-b flex-shrink-0"
              style={{ borderColor: 'var(--border-color)' }}
            >
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2
                    className="text-lg font-semibold mb-1 flex items-center gap-2.5"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    <Lightbulb className="w-5 h-5" style={{ color: 'var(--accent)' }} />
                    <span>Coach Support</span>
                  </h2>
                  <p
                    className="text-xs font-medium"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    Client guiding assistant
                  </p>
                </div>
                <button
                  onClick={() => setChatExpanded(!chatExpanded)}
                  className="p-2 rounded-lg transition-all duration-200"
                  style={{ color: 'var(--text-tertiary)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                    e.currentTarget.style.color = 'var(--text-primary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = 'var(--text-tertiary)';
                  }}
                  title={chatExpanded ? 'Collapse chat panel' : 'Expand chat panel'}
                >
                  {chatExpanded ? (
                    <Minimize2 className="w-4 h-4" />
                  ) : (
                    <Maximize2 className="w-4 h-4" />
                  )}
                </button>
              </div>

              {/* Private Notes Toggle */}
              <button
                onClick={() => setIncludePrivateNotes(!includePrivateNotes)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-all"
                style={{
                  backgroundColor: includePrivateNotes ? 'rgba(245, 158, 11, 0.15)' : 'var(--bg-secondary)',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: includePrivateNotes ? 'rgba(245, 158, 11, 0.3)' : 'var(--border-color)',
                  color: includePrivateNotes ? 'rgb(245, 158, 11)' : 'var(--text-secondary)'
                }}
                title={includePrivateNotes ? 'AI can see your private notes' : 'AI cannot see your private notes'}
              >
                <span className="flex items-center gap-2">
                  <Lock className="w-3.5 h-3.5" />
                  <span>Include my private notes in this conversation?</span>
                </span>
                <span
                  className="px-2 py-0.5 rounded text-xs font-semibold"
                  style={{
                    backgroundColor: includePrivateNotes ? 'rgb(245, 158, 11)' : 'var(--bg-tertiary)',
                    color: includePrivateNotes ? '#ffffff' : 'var(--text-tertiary)'
                  }}
                >
                  {includePrivateNotes ? 'ON' : 'OFF'}
                </span>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0 space-y-4">
              {chatMessages.length === 0 ? (
              <div className="text-center mt-20" style={{ color: 'var(--text-tertiary)' }}>
                  <Lightbulb className="w-12 h-12 mx-auto mb-4 opacity-50" style={{ color: 'var(--text-tertiary)' }} />
                  <p className="text-base font-semibold" style={{ color: 'var(--text-secondary)' }}>Start a conversation</p>
                  <p className="text-sm mt-2" style={{ color: 'var(--text-tertiary)' }}>Ask about your client's progress, objectives, or session planning</p>
                </div>
              ) : (
                chatMessages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in duration-300`}
                  >
                    <div
                      className={`rounded-lg px-5 py-3.5 transition-all duration-200 ${
                        msg.role === 'user' || msg.error
                          ? 'max-w-[80%]'
                          : chatExpanded
                          ? 'max-w-[90%]'
                          : 'max-w-[80%]'
                      }`}
                      style={{
                        backgroundColor: msg.role === 'user'
                          ? 'var(--accent)'
                          : msg.error
                          ? 'rgba(239, 68, 68, 0.15)'
                          : 'var(--bg-secondary)',
                        color: msg.role === 'user'
                          ? '#ffffff'
                          : msg.error
                          ? '#fca5a5'
                          : 'var(--text-primary)',
                        borderColor: msg.role === 'user'
                          ? 'var(--accent)'
                          : msg.error
                          ? 'rgba(239, 68, 68, 0.2)'
                          : 'var(--border-color)',
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        boxShadow: msg.role === 'user'
                          ? 'none'
                          : 'var(--shadow-sm)'
                      }}
                    >
                      {msg.role === 'user' ? (
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      ) : (
                        <div className={`prose prose-invert max-w-none ${chatExpanded ? 'prose-base' : 'prose-sm'}`}>
                          <ReactMarkdown
                            components={{
                              h1: ({node, ...props}) => <h1 className={`font-bold mb-2 mt-3 ${chatExpanded ? 'text-xl' : 'text-lg'}`} style={{ color: 'var(--text-primary)' }} {...props} />,
                              h2: ({node, ...props}) => <h2 className={`font-semibold mb-2 mt-3 ${chatExpanded ? 'text-lg' : 'text-base'}`} style={{ color: 'var(--text-primary)' }} {...props} />,
                              h3: ({node, ...props}) => <h3 className={`font-semibold mb-1.5 mt-2 ${chatExpanded ? 'text-base' : 'text-sm'}`} style={{ color: 'var(--accent)' }} {...props} />,
                              h4: ({node, ...props}) => <h4 className={`font-medium mb-1 mt-2 ${chatExpanded ? 'text-sm' : 'text-xs'}`} style={{ color: 'var(--text-secondary)' }} {...props} />,
                              p: ({node, ...props}) => <p className={`mb-2 leading-relaxed ${chatExpanded ? 'text-base' : 'text-sm'}`} style={{ color: 'var(--text-primary)' }} {...props} />,
                              strong: ({node, ...props}) => <strong className="font-semibold" style={{ color: 'var(--text-primary)' }} {...props} />,
                              em: ({node, ...props}) => <em className="italic" style={{ color: 'var(--text-secondary)' }} {...props} />,
                              ul: ({node, ...props}) => <ul className={`list-disc mb-3 ml-4 space-y-2 ${chatExpanded ? 'text-base' : 'text-sm'}`} style={{ color: 'var(--text-primary)' }} {...props} />,
                              ol: ({node, ...props}) => <ol className={`list-decimal mb-3 ml-4 space-y-2 ${chatExpanded ? 'text-base' : 'text-sm'}`} style={{ color: 'var(--text-primary)' }} {...props} />,
                              li: ({node, ...props}) => <li className="leading-relaxed pl-1" style={{ color: 'var(--text-primary)' }} {...props} />,
                              code: ({node, ...props}) => <code className={`bg-black/30 px-1.5 py-0.5 rounded ${chatExpanded ? 'text-sm' : 'text-xs'}`} style={{ color: 'var(--accent)' }} {...props} />,
                              blockquote: ({node, ...props}) => <blockquote className={`border-l-2 pl-3 italic my-2 ${chatExpanded ? 'pl-4' : 'pl-3'}`} style={{ borderColor: 'var(--accent-50)', color: 'var(--text-secondary)' }} {...props} />,
                            }}
                          >
                            {msg.content}
                          </ReactMarkdown>
                        </div>
                      )}
                      <p className="text-xs mt-2.5 opacity-50 font-medium">
                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))
              )}
              {chatLoading && (
                <div className="flex justify-start animate-in fade-in duration-200">
                  <div 
                    className="rounded-lg px-5 py-3 shadow-sm"
                    style={{
                      backgroundColor: 'var(--bg-secondary)',
                      color: 'var(--text-secondary)',
                      borderColor: 'var(--border-color)',
                      borderWidth: '1px',
                      borderStyle: 'solid'
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="animate-spin rounded-full h-4 w-4 border-2" style={{ borderColor: 'var(--accent-50)', borderTopColor: 'var(--accent)' }}></div>
                      <span className="text-sm font-medium">Thinking...</span>
                    </div>
                  </div>
              </div>
              )}
              <div ref={chatEndRef} />
            </div>
            <div 
              className="px-6 py-4 border-t flex-shrink-0"
              style={{
                borderColor: 'var(--border-color)',
                backgroundColor: 'var(--bg-primary)'
              }}
            >
              <form onSubmit={handleSendChatMessage} className="flex gap-3">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Ask about your client..."
                  disabled={chatLoading}
                  className="flex-1 px-4 py-3 rounded-lg focus:outline-none text-sm font-medium disabled:opacity-50 transition-all duration-200"
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    borderColor: 'var(--border-color)',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    boxShadow: 'var(--shadow-sm)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--accent)';
                    e.target.style.boxShadow = '0 0 0 3px var(--accent-15)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--border-color)';
                    e.target.style.boxShadow = 'var(--shadow-sm)';
                  }}
                />
                <button
                  type="submit"
                  disabled={chatLoading || !chatInput.trim()}
                  className="text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: 'var(--accent)',
                    boxShadow: 'var(--shadow-md)'
                  }}
                  onMouseEnter={(e) => {
                    if (!chatLoading && chatInput.trim()) {
                      e.currentTarget.style.backgroundColor = 'var(--accent-hover)';
                      e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--accent)';
                    e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                  }}
                >
                  Send
                </button>
              </form>
            </div>
          </div>
        </div>
      ) : selectedClient && loading ? (
        <div className="flex-1 flex justify-center items-center">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4" style={{ borderColor: 'var(--border-color)', borderTopColor: 'var(--accent)' }}></div>
            <p className="text-sm font-medium" style={{ color: 'var(--text-tertiary)' }}>Loading client data...</p>
          </div>
        </div>
      ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
          <div className="max-w-md">
            <h2 className="text-3xl font-bold mb-3 tracking-tight" style={{ color: 'var(--text-primary)' }}>Welcome to RMMBR Method Sessions</h2>
            <p className="text-base font-medium mb-6" style={{ color: 'var(--text-tertiary)' }}>Select a client from the top navigation to begin working with their coaching data</p>
            <div className="flex flex-wrap gap-2 justify-center text-sm" style={{ color: 'var(--text-tertiary)' }}>
              <span className="px-3 py-1.5 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', borderWidth: '1px', borderStyle: 'solid' }}>Session Notes</span>
              <span className="px-3 py-1.5 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', borderWidth: '1px', borderStyle: 'solid' }}>Objectives</span>
              <span className="px-3 py-1.5 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', borderWidth: '1px', borderStyle: 'solid' }}>AI Insights</span>
            </div>
          </div>
        </div>
      )}

      {/* Profile Settings Modal */}
      <ProfileSettings
        user={user}
        isOpen={showProfileSettings}
        onClose={() => setShowProfileSettings(false)}
      />
    </div>
  );
}

export default CoachDashboard;
